import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { OLAF, OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import { useSelector } from 'react-redux';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { SUITE_CARD } from '../../constants/tracking/Locations';
import { ownsCrmSuiteProductLevel, productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { useQueryParamTerm } from '../../utils/urls/queryParamUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import { SectionTitle } from '../common/SharedComponents';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import { StyledBlob } from '../product-pages/StarterCrmSuiteCtaCard';
import { MerchCardWrapper, Section } from './MerchCardSharedComponents';
import SuiteTermSelectorContainer from './SuiteTermSelectorContainer';
import SuiteTileCTAsContainer from './SuiteTileCTAsContainer';
import SuiteBusinessObjectives from './business-objectives/SuiteBusinessObjectives';
import { CrmSuitePrice } from './price-display/CrmSuitePrice'; // @ts-expect-error svg import

import BLOB from 'bender-url!pricing-pages-ui/img/splash-page/suite-card-top-right-blob.svg';
import { isAnyStarterGWP2023EligibleSelector } from '../../selectors/PromotionSelectors';
import SuiteFeatureTableBadge from '../common/SuiteFeatureTableBadge';

const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case FREE:
      return {
        titleKey: 'merchCards.suiteTile.freeCard.title',
        subtitleKey: 'merchCards.suiteTile.freeCard.description'
      };

    case STARTER:
      return {
        titleKey: 'merchCards.suiteTile.starterCard.title',
        subtitleKey: 'merchCards.suiteTile.starterCard.description'
      };

    case PROFESSIONAL:
      return {
        titleKey: 'merchCards.suiteTile.professionalCard.title',
        subtitleKey: 'merchCards.suiteTile.professionalCard.description'
      };

    case ENTERPRISE:
      return {
        titleKey: 'merchCards.suiteTile.enterpriseCard.title',
        subtitleKey: 'merchCards.suiteTile.enterpriseCard.description'
      };

    default:
      return {
        titleKey: '',
        subtitleKey: ''
      };
  }
};

export const SuiteTile = ({
  productLevel,
  commonStyles,
  isFreeToStarter,
  disclaimer
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const isStarterGWP = useSelector(isAnyStarterGWP2023EligibleSelector);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const ownedProducts = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return [];
    return productsFromProductInventorySelector(state);
  });
  const isCurrentPlanLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  const isFreePortal = ownedProducts && ownedProducts.length <= 0;
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    isFreeToStarter: isFreeToStarter,
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      className: "flex flex-column p-top-8",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [isCurrentPlanLevel && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsx("div", {
        className: "display-flex align-center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey
          })
        })
      }), isFreePortal && productLevel === STARTER && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(StyledBlob, {
          src: BLOB
        }), /*#__PURE__*/_jsx(SuiteFeatureTableBadge, {
          badgeStyle: {
            top: '12px',
            right: '12px'
          }
        })]
      }), !isFreeToStarter && isStarterGWP && productLevel === STARTER && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: "badges.newCustomerOffer",
        tagStyle: {
          backgroundColor: OZ,
          color: OLAF
        }
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        zIndex: 1
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        className: "flex-column",
        Content: CrmSuitePrice,
        contentProps: {
          productLevel,
          useLargePrice: true
        }
      })
    }), !isSeatsRedesignUngated && /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: !isCurrentPlanLevel && /*#__PURE__*/_jsx("div", {
        className: "m-top-3",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [41],
          Content: SuiteTermSelectorContainer,
          contentProps: {
            productLevel,
            onChange: term => setQueryParamTerm(term),
            queryParamTerm,
            trackingSource: SUITE_CARD
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 p-bottom-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
          productLevel: productLevel,
          trackingSource: SUITE_CARD,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          },
          returnFirstCTAOnly: isFreeToStarter,
          isFreeToStarter: isFreeToStarter
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex flex-column",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !hasPricingAndPurchasingSucceeded,
      heights: [47],
      className: "m-bottom-7",
      children: /*#__PURE__*/_jsx(SuiteBusinessObjectives, {
        productLevel: productLevel,
        commonStyles: commonStyles
      })
    }), /*#__PURE__*/_jsx(Section, {
      style: Object.assign({}, commonStyles, {
        gridRowStart: 10
      }),
      children: disclaimer && /*#__PURE__*/_jsx(Small, {
        use: "help",
        className: "align-end p-bottom-6",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${disclaimer}`
        })
      })
    })]
  });
};